import { keyBy } from 'lodash/fp';

export type UpdateResult = 'unchanged' | 'updated' | 'recreate';

export interface SyncConfig<TSource, TScene> {
  getSourceItems: () => TSource[] | null | undefined;
  getSceneItems: () => TScene[];
  getSourceIdentifier: (item: TSource) => string;
  getSceneIdentifier: (item: TScene) => string;
  /**
   * Called when a source item has a matching scene item.
   * - 'unchanged': skip (nothing to do)
   * - 'updated': the scene item was modified in-place (skip recreate)
   * - 'recreate': the scene item must be removed and recreated
   *
   * Optional — when omitted, existing items are always treated as 'unchanged'
   * (useful when geometry updates are handled by reactive property subscriptions).
   */
  update?: (source: TSource, scene: TScene) => UpdateResult;
  create: (source: TSource, existingScene?: TScene) => TScene;
  addToScene: (item: TScene) => void;
  removeFromScene: (item: TScene) => void;
  /** Called after a newly created item is added to the scene. */
  afterCreate?: (item: TScene, source: TSource) => void;
  /** When set, spread creation across frames in batches of this size */
  chunkSize?: number;
}

export function syncFromGraphQL<TSource, TScene>(config: SyncConfig<TSource, TScene>): void {
  const sourceItems = config.getSourceItems();
  if (!sourceItems) return;

  const sceneItems = config.getSceneItems();
  const sceneByIdentifier = keyBy(config.getSceneIdentifier, sceneItems);
  const sourceByIdentifier = keyBy(config.getSourceIdentifier, sourceItems);

  // Removals always run synchronously (must be atomic)
  for (const scene of sceneItems) {
    const id = config.getSceneIdentifier(scene);
    if (!sourceByIdentifier[id]) {
      config.removeFromScene(scene);
    }
  }

  // Collect items that need to be created
  const toCreate: Array<{ source: TSource; existing?: TScene }> = [];
  for (const source of sourceItems) {
    const id = config.getSourceIdentifier(source);
    const existing = sceneByIdentifier[id] as TScene | undefined;

    if (existing) {
      const result = config.update ? config.update(source, existing) : 'unchanged';
      if (result !== 'recreate') continue;
      config.removeFromScene(existing);
    }

    toCreate.push({ source, existing });
  }

  // Create items — chunked across frames when chunkSize is set, otherwise synchronous
  const chunkSize = config.chunkSize;
  if (chunkSize && toCreate.length > chunkSize) {
    let offset = 0;
    const processChunk = () => {
      const end = Math.min(offset + chunkSize, toCreate.length);
      for (let i = offset; i < end; i++) {
        const { source, existing } = toCreate[i];
        const created = config.create(source, existing);
        config.addToScene(created);
        config.afterCreate?.(created, source);
      }
      offset = end;
      if (offset < toCreate.length) {
        requestAnimationFrame(processChunk);
      }
    };
    processChunk();
  } else {
    for (const { source, existing } of toCreate) {
      const created = config.create(source, existing);
      config.addToScene(created);
      config.afterCreate?.(created, source);
    }
  }
}
